
import { useState } from "react";
import { Globe, Plus, Trash2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery as useConvexQuery, useMutation as useConvexMutation } from "convex/react";
import { api } from "../../api";
import { AddUrlModal, type UrlRule } from "./AddUrlModal";
import { useToast } from "@/hooks/use-toast";

// Wildcard app ID - URLs added to any app should also be added here
const WILDCARD_APP_ID = "j57fgp9z6fejkw5frve4bnx1957m90gc";

interface App {
  id: string;
  bundle_id: string;
  app_name: string;
}

interface AppUrlsTabProps {
  app: App;
}

export const AppNetworkRulesTab = ({ app }: AppUrlsTabProps) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const { toast } = useToast();
  
  // Fetch app details to get webkit URLs with proper refresh
  const appDataArray = useConvexQuery(api.apps.get.apps, { apps: [app.id as any] });
  const appData = appDataArray?.[0];
  const isLoading = appDataArray === undefined;

  // Fetch wildcard app data (skip if we're already editing the wildcard app)
  const wildcardAppDataArray = useConvexQuery(
    api.apps.get.apps,
    app.id !== WILDCARD_APP_ID ? { apps: [WILDCARD_APP_ID as any] } : "skip"
  );
  const wildcardAppData = wildcardAppDataArray?.[0];
  const wildcardUrls: string[] = (wildcardAppData as any)?.webkit || [];
  
  // DEBUG: Log API response
  console.log('🔍 DEBUG - api.apps.get.apps response:', {
    fullResponse: appDataArray,
    appData: appData,
    appId: app.id,
    rawWebkit: (appData as any)?.webkit,
    webkitType: typeof (appData as any)?.webkit,
    webkitLength: (appData as any)?.webkit?.length,
    allAppDataKeys: appData ? Object.keys(appData) : 'No app data'
  });
  
  // DEBUG: Complete field analysis
  console.log('📋 DEBUG - Complete API fields analysis:', {
    hasWebkit: appData && 'webkit' in appData,
    allFields: appData ? Object.keys(appData).sort() : [],
    fieldsWithArrayValues: appData ? Object.keys(appData).filter(key => Array.isArray(appData[key])) : [],
    fieldsWithStringValues: appData ? Object.keys(appData).filter(key => typeof appData[key] === 'string') : [],
    sampleFieldValues: appData ? Object.fromEntries(
      Object.entries(appData).slice(0, 10).map(([key, value]) => [
        key, 
        Array.isArray(value) ? `[Array of ${value.length} items]` : 
        typeof value === 'object' ? '[Object]' : 
        String(value).substring(0, 50)
      ])
    ) : 'No data'
  });
  
  // Ensure we have the latest webkit URLs
  const urls = (appData as any)?.webkit || [];
  
  // DEBUG: Log processed URLs
  console.log('🌐 DEBUG - Processed URLs:', {
    urls: urls,
    urlsCount: urls.length,
    urlsType: typeof urls,
    isArray: Array.isArray(urls)
  });

  // Mutation to update webkit URLs
  const updateWebkitMutation = useConvexMutation(api.apps.update.webkit);

  const handleAddUrl = async (urlRule: UrlRule) => {
    try {
      const currentUrls = urls || [];
      
      // Check if URL already exists to prevent duplicates
      if (currentUrls.includes(urlRule.url)) {
        toast({
          title: "URL already exists",
          description: `"${urlRule.url}" is already in the app's URL list.`,
          variant: "destructive",
        });
        return;
      }
      
      const newUrls = [...currentUrls, urlRule.url];
      
      await updateWebkitMutation({
        app: app.id as any,
        webkit: newUrls
      });

      // Also add to wildcard app if:
      // 1. We're not already editing the wildcard app
      // 2. The URL doesn't already exist in the wildcard app
      if (app.id !== WILDCARD_APP_ID && !wildcardUrls.includes(urlRule.url)) {
        await updateWebkitMutation({
          app: WILDCARD_APP_ID as any,
          webkit: [...wildcardUrls, urlRule.url]
        });
        console.log('URL also added to wildcard app:', urlRule.url);
      }

      toast({
        title: "URL added successfully",
        description: `Added "${urlRule.url}" to the app's webkit URLs.`,
      });

      setIsAddModalOpen(false);
    } catch (error) {
      console.error('Error adding URL:', error);
      toast({
        title: "Error adding URL",
        description: "Failed to add the URL. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleRemoveUrl = async (urlToRemove: string) => {
    try {
      const currentUrls = urls || [];
      const newUrls = currentUrls.filter((url: string) => url !== urlToRemove);
      
      await updateWebkitMutation({
        app: app.id as any,
        webkit: newUrls
      });

      // Also remove from wildcard app if:
      // 1. We're not already editing the wildcard app
      // 2. The URL exists in the wildcard app
      if (app.id !== WILDCARD_APP_ID && wildcardUrls.includes(urlToRemove)) {
        const newWildcardUrls = wildcardUrls.filter((url: string) => url !== urlToRemove);
        await updateWebkitMutation({
          app: WILDCARD_APP_ID as any,
          webkit: newWildcardUrls
        });
        console.log('URL also removed from wildcard app:', urlToRemove);
      }

      toast({
        title: "URL removed successfully",
        description: `Removed "${urlToRemove}" from the app's webkit URLs.`,
      });
    } catch (error) {
      console.error('Error removing URL:', error);
      toast({
        title: "Error removing URL",
        description: "Failed to remove the URL. Please try again.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-gray-500">Loading URLs...</div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto">
      <div className="p-3 space-y-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-lg font-semibold flex items-center">
              <Globe className="w-5 h-5 mr-2" />
              URLs ({urls.length})
            </CardTitle>
            <Button onClick={() => setIsAddModalOpen(true)} size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Add URL
            </Button>
          </CardHeader>
          <CardContent>
            {urls.length === 0 ? (
              <div className="text-center py-12">
                <Globe className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No URLs Configured</h3>
                <p className="text-gray-500">
                  There are currently no webkit URLs configured for this app.
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>URL</TableHead>
                    <TableHead className="w-[100px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {urls.map((url: string, index: number) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div className="flex items-center">
                          <Globe className="w-4 h-4 text-gray-400 mr-3" />
                          <span className="font-mono text-sm">{url}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleRemoveUrl(url)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Add URL Modal */}
        <AddUrlModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          onAddUrl={handleAddUrl}
          ruleType="webkit"
        />
      </div>
    </div>
  );
};
